import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../shared/dataTable/index";
import{ ShopCreateDTO, ShopUpdateDTO,Store } from "../../../types/shop.types";
import shopService from "../../../services/shop/shop.service";
import { Popconfirm, message, Tag, Input, Button, Switch, InputNumber, AutoComplete, Spin, Form, Row, Col, Typography, Card, Space } from 'antd';
import { PlusOutlined, HomeOutlined, PhoneOutlined, PushpinOutlined, GlobalOutlined, ShopOutlined, EditOutlined, EnvironmentOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../shared/uiInterface/base-ui/LoadingSpinner";
import { exportToExcel, normalizeTurkishChars } from "../../../utils/exportUtils";
import { createPdf } from "../../../utils/exportUtils";
import { handleApiError } from "../../../utils/errorHandler";
import dayjs from "dayjs";
import FooterSection from "../../shared/components/footerSection";
import { MapContainer, Marker, useMapEvents } from 'react-leaflet';
import YandexTileLayer from '../../shared/components/maps/YandexTileLayer';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { PermissionType } from "../../../types/user.types";

// Leaflet'in Webpack ile uyumlu çalışması için ikon yolu düzeltmesi
// eslint-disable-next-line @typescript-eslint/no-var-requires
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});


type AddressOptionType = {
  value: string;
  label: React.ReactNode;
  lat?: string;
  lon?: string;
  disabled?: boolean;
};

const { Text, Title } = Typography;

const defaultFormState: ShopCreateDTO = {
  name: "",
  address: "",
  phone: "",
  ipAddresses: [],
  enableLocationBasedCheckout: false,
  latitude: undefined,
  longitude: undefined,
  locationTolerance: 1000,
};


const Shop = () => {
  const { t } = useTranslation();
  const { createShop, getShops, updateShop, deleteShop } = shopService;
  const [formState, setFormState] = useState<ShopCreateDTO>(defaultFormState);
  const [shops, setShops] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Store | null>(null);
  const [newIpAddress, setNewIpAddress] = useState<string>("");
  const [addressOptions, setAddressOptions] = useState<AddressOptionType[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const [mapKey, setMapKey] = useState(0); // Harita re-render için
  let debounceTimeout: NodeJS.Timeout | undefined;
  const { user } = useSelector((state: RootState) => state.auth);
  useEffect(() => {
    if (selectedShop && selectedShop.latitude && selectedShop.longitude) {
      setMarkerPosition([selectedShop.latitude, selectedShop.longitude]);
    } else if (!selectedShop) {
      setMarkerPosition(null);
      setIsMapVisible(false);
    }
  }, [selectedShop]);

  // Harita tıklama olaylarını yöneten bileşen
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        handleLocationSelect(lat, lng);
      },
    });
    return null;
  };

  // Konum seçildiğinde çalışacak fonksiyon
  const handleLocationSelect = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);
    
    // Koordinatları güncelle
    setFormState(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));

    // Reverse geocoding ile adresi güncelle
    updateAddressFromCoordinates(lat, lng);
  };

  const handleAddressSearch = async (searchText: string) => {
    if (!searchText || searchText.length < 3) {
      setAddressOptions([]);
      return;
    }
    
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(async () => {
      setIsSearchingAddress(true);
      try {
        // Türkiye odaklı arama için parametreler eklendi
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&limit=8&countrycodes=tr&addressdetails=1&dedupe=1`
        );
        const data = await response.json();
        
        const options = data.map((item: any) => ({
          value: item.display_name,
          label: (
            <div style={{ padding: '4px 0' }}>
              <div style={{ fontWeight: '500', fontSize: '14px', color: '#262626' }}>
                {item.name || item.display_name.split(',')[0]}
              </div>
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                {item.display_name}
              </div>
            </div>
          ),
          lat: item.lat,
          lon: item.lon,
        }));
        setAddressOptions(options);
      } catch (error) {
        setAddressOptions([]);
      } finally {
        setIsSearchingAddress(false);
      }
    }, 300); // 300ms debounce - daha hızlı
  };

  const onAddressSelect = (value: string, option: AddressOptionType) => {
    // Sadece lat ve lon değerleri olan geçerli bir adres seçildiğinde enlem/boylamı güncelle.
    if (option.lat && option.lon) {
      const lat = parseFloat(option.lat);
      const lon = parseFloat(option.lon);
      
      setFormState({
        ...formState,
        address: value,
        latitude: lat,
        longitude: lon,
      });
      
      // Harita işaretçisini güncelle
      setMarkerPosition([lat, lon]);
    }
  };

  // Koordinat değiştiğinde adresi güncelleme fonksiyonu
  const updateAddressFromCoordinates = async (lat: number, lon: number) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
      const data = await response.json();
      if (data && data.display_name) {
        setFormState(prev => ({
          ...prev,
          address: data.display_name,
        }));
      }
    } catch (error) {
      // console.error('Koordinatlardan adres güncellenirken hata:', error);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      // 1 saniyelik gecikme ekleyerek yükleme göstergesinin görünmesini sağlayalım
      setTimeout(async () => {
        try {
          const result = await getShops();
          setShops(result);
        } catch (err: any) {
          handleApiError(err,t);
        } finally {
          setLoading(false);
        }
      }, 1000);
    } catch (err: any) {
      handleApiError(err,t);
    }
  };

  // IP adresi eklemek için fonksiyon
  const handleAddIpAddress = () => {
    const trimmedIp = newIpAddress.trim();
    
    if (!trimmedIp) {
      message.error('IP adresi boş olamaz');
      return;
    }

    // IP adresini doğrula
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(trimmedIp)) {
      message.error('Geçersiz IP adresi formatı. Örnek: 192.168.1.1');
      return;
    }

    // IP adresi zaten varsa ekleme
    if (formState.ipAddresses.includes(trimmedIp)) {
      message.warning(`IP adresi ${trimmedIp} zaten mevcut`);
      return;
    }

    // IP adresini ekle
    const updatedIps = [...formState.ipAddresses, trimmedIp];
    setFormState({ ...formState, ipAddresses: updatedIps });
    setNewIpAddress(""); // Input temizle
    
    message.success(`IP adresi ${trimmedIp} eklendi`);
  };

  // IP adresini silmek için fonksiyon
  const handleRemoveIpAddress = (ipToRemove: string) => {
    
    // Sadece ilk eşleşen IP'yi sil
    const currentIps = [...formState.ipAddresses];
    const indexToRemove = currentIps.findIndex(ip => ip === ipToRemove);
    
    if (indexToRemove > -1) {
      currentIps.splice(indexToRemove, 1);
      setFormState({ ...formState, ipAddresses: currentIps });
      message.success(`IP adresi ${ipToRemove} kaldırıldı`);
    } else {
      message.error('IP adresi bulunamadı');
    }
  };

  const handleSave = async () => {
    try {
      // Form validasyonu
      if (!formState.name || formState.name.trim() === '') {
        message.error(t('branches.branchNameError'));
        return;
      }
      if (formState.name.length < 2) {
        message.error(t('branches.branchNameError1'));
        return;
      }
      if (formState.name.length > 50) {
        message.error(t('branches.branchNameError2'));
        return;
      }

      // Adres validasyonu
      if (!formState.address || formState.address.trim() === '') {
        message.error(t('branches.branchAddressError'));
        return;
      }
      if (formState.address.length < 5) {
        message.error(t('branches.branchAddressError1'));
        return;
      }

      // Telefon validasyonu
      if (!formState.phone || formState.phone.trim() === '') {
        message.error(t('branches.branchPhoneError'));
        return;
      }
      const phoneRegex = /^[0-9]{10,11}$/;
      if (!phoneRegex.test(formState.phone.replace(/\D/g, ''))) {
        message.error(t('branches.branchPhoneError1'));
        return;
      }

      if (selectedShop) {
        const updateDto: ShopUpdateDTO = {
          ...formState,
          id: selectedShop.id
        };
        await updateShop(selectedShop.id, updateDto);
        message.success(t('branches.branchUpdateSuccess'));
      } else {
        const createDto = formState as ShopCreateDTO;
        await createShop(createDto);
        message.success(t('branches.branchSuccess'));
      }
      fetchShops();
      handleClear();
    } catch (error: any) {
      handleApiError(error,t);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteShop(id);
      message.success('Şube başarıyla silindi');
      fetchShops();
      handleClear();
    } catch (error: any) {
      handleApiError(error,t);
    }
  };

  const handleClear = () => {
    setSelectedShop(null);
    setFormState(defaultFormState);
    setNewIpAddress("");
  };

  const handleSelectShop = (shop: Store) => {
    setSelectedShop(shop);
    setFormState({
      name: shop.name,
      address: shop.address,
      phone: shop.phone,
      ipAddresses: shop.ipAddresses || [],
      enableLocationBasedCheckout: shop.enableLocationBasedCheckout || false,
      latitude: shop.latitude,
      longitude: shop.longitude,
      locationTolerance: shop.locationTolerance,
    });
  };

  // Mevcut şubenin bilgileriyle yeni şube formunu doldurur; kaydedilince ayrı bir şube oluşur
  const handleDuplicateShop = (shop: Store) => {
    const copySuffix = ' (Kopya)';
    setSelectedShop(null);
    setNewIpAddress("");
    setIsMapVisible(false);
    setMarkerPosition(shop.latitude && shop.longitude ? [shop.latitude, shop.longitude] : null);
    setFormState({
      name: `${shop.name.slice(0, 50 - copySuffix.length)}${copySuffix}`,
      address: shop.address,
      phone: shop.phone,
      ipAddresses: [...(shop.ipAddresses || [])],
      enableLocationBasedCheckout: shop.enableLocationBasedCheckout || false,
      latitude: shop.latitude,
      longitude: shop.longitude,
      locationTolerance: shop.locationTolerance,
    });
  };

  const columns = [
    {
      title: t('branches.table.name'),
      dataIndex: "name",
      render: (text: string, record: Store) => (
        <Link 
        to={`${all_routes.employeeList}?branch=${record.id}`}>
          {text}
        </Link>
      ),
      //sorter: (a: any, b: any) => a.storeName.localeCompare(b.storeName),
    },
    {
      title: t('branches.table.address'),
      dataIndex: "address",
      render: (address: string) => (
        address?.length > 30 ? (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip title={address} placement="top">{address}</Tooltip>}
          >
            <div className="position-relative">
              <p className="fs-14 mb-0"
                  style={{ 
                    maxWidth: "200px", 
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight: "22px"
                  }}
              >{address}</p>
              <span className="text-primary" style={{ position: 'absolute', right: 0, top: 0, width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', opacity: 0.8 }}>
                <i className="ti ti-info-circle" />
              </span>
            </div>
          </OverlayTrigger>
        ) : address
      )
      //sorter: (a: any, b: any) => a.storeLocation.localeCompare(b.storeLocation),
    },
    {
      title: t('branches.table.phone'),
      dataIndex: "phone",
      sorter: (a: any, b: any) => a.phone.localeCompare(b.phone),
    },
    {
      title: t('branches.table.numberOfEmployees'),
      dataIndex: "employeeCount",
      sorter: (a: any, b: any) => a.employeeCount - b.employeeCount,
    },
    {
      title: t('branches.table.ipAddresses'),
      dataIndex: "ipAddresses",
      render: (ipAddresses: string[]) => (
        <div>
          {ipAddresses && ipAddresses.length > 0 ? (
            <div className="d-flex flex-wrap gap-1">
              {ipAddresses.map(ip => (
                <Tag key={ip} color="blue">{ip}</Tag>
              ))}
            </div>
          ) : (
            <span className="text-muted">{t('branches.modal.noIpAddresses')}</span>
          )}
        </div>
      ),
    },
    {
      title: t('branches.table.actions'),
      render: (record: Store) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#add_store_modal"
            onClick={() => {
              handleSelectShop(record)
            }}
          >
            <i className="ti ti-edit" />
          </button>

          {user?.permissions?.includes(PermissionType.EDIT_BRANCHES) && (
          <button
            className="btn btn-sm btn-white border"
            title="Kopyala"
            data-bs-toggle="modal"
            data-bs-target="#add_store_modal"
            onClick={() => handleDuplicateShop(record)}
          >
            <i className="ti ti-copy" />
          </button>
          )}

          {user?.permissions?.includes(PermissionType.EDIT_BRANCHES) && (
          <Popconfirm
            title={t('branches.branchDeleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('branches.ok')}
            cancelText={t('branches.cancel')}
          >
            <button
              className="btn btn-sm btn-danger"
            >
              <i className="ti ti-trash" />
            </button>
          </Popconfirm>
          )}
        </div>
      ),
    },

  ];


  
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{t('branches.pageTitle')}</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">{t('branches.breadcrumb.management')}</li>
                  <li className="breadcrumb-item active">{t('branches.breadcrumb.branches')}</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <button
                className="btn btn-sm btn-primary d-inline-flex align-items-center me-2"
                data-bs-toggle="modal"
                data-bs-target="#add_store_modal"
                onClick={handleClear} // Yeni mağaza için varsayılan değerler
              >
                <i className="ti ti-plus me-1" /> {t('branches.addBranch')}
              </button>
              {user?.permissions?.includes(PermissionType.EXPORT) && (
              <div className="dropdown me-2">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-file-export me-1" />
                  {t('common.buttons.export')}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link 
                    to="#" 
                    className="dropdown-item rounded-2 d-flex align-items-center py-2"
                    onClick={() => {
                      // Şubeler için sütun tanımları
                      type BranchDataType = {
                        name: string;
                        address: string;
                        phone: string;
                        employeeCount: string;
                        ipAddresses: string;
                      };

                      // PDF için sütun tanımları - Landscape A4 genişliğine göre optimize edildi (297mm, margin'lerle ~281mm)
                      const columnsForPdf = [
                        { header: normalizeTurkishChars('Şube Adı'), property: 'name' as keyof BranchDataType, width: 35, align: 'left' as const },
                        { header: normalizeTurkishChars('Adres'), property: 'address' as keyof BranchDataType, width: 120, align: 'left' as const },
                        { header: normalizeTurkishChars('Telefon'), property: 'phone' as keyof BranchDataType, width: 35, align: 'left' as const },
                        { header: normalizeTurkishChars('Çalışan Sayısı'), property: 'employeeCount' as keyof BranchDataType, width: 30, align: 'center' as const },
                        { header: normalizeTurkishChars('IP Adresleri'), property: 'ipAddresses' as keyof BranchDataType, width: 50, align: 'left' as const }
                      ];

                      // PDF için formatlanmış veri
                      const formattedData = shops.map(shop => ({
                        name: normalizeTurkishChars(shop.name) || '-',
                        address: normalizeTurkishChars(shop.address) || '-',
                        phone: shop.phone || '-',
                        employeeCount: String(shop.employeeCount || 0),
                        ipAddresses: shop.ipAddresses && shop.ipAddresses.length > 0 
                          ? normalizeTurkishChars(shop.ipAddresses.join(', '))
                          : '-'
                      }));

                      // Açıklama
                      const description = normalizeTurkishChars('Şube listesi ve detayları');

                      // createPdf fonksiyonunu çağır
                      createPdf(
                        formattedData, 
                        {
                          title: normalizeTurkishChars('Şube Listesi'), 
                          description: description,
                          headerColor: '#2c3e50', // Koyu lacivert
                          accentColor: '#3498db', // Mavi
                          columns: columnsForPdf,
                          showStatistics: false, // İstatistik kutuları gösterme
                          footer: {
                            text: `© ${new Date().getFullYear()} ${normalizeTurkishChars('DINOX')} - ${normalizeTurkishChars('Tum Haklari Saklidir')}`,
                            showPageNumbers: true
                          }
                        }
                      );
                    }}
                    >
                      <i className="ti ti-file-type-pdf me-2 text-danger" />
                      {t('common.buttons.pdf')}
                    </Link>
                  </li>
                  <li className="mt-1">
                    <Link 
                    to="#" 
                    className="dropdown-item rounded-2 d-flex align-items-center py-2"
                    onClick={() => {
                      // Excel için sütun tanımları
                      type BranchExcelType = {
                        id: number;
                        name: string;
                        address: string;
                        phone: string;
                        employeeCount: number;
                        ipAddresses: string;
                        createdAt?: string;
                      };

                      const columnsForExcel = [
                        { header: 'ID', property: 'id' as keyof BranchExcelType, width: 10 },
                        { header: 'Şube Adı', property: 'name' as keyof BranchExcelType, width: 30 },
                        { header: 'Adres', property: 'address' as keyof BranchExcelType, width: 50 },
                        { header: 'Telefon', property: 'phone' as keyof BranchExcelType, width: 20 },
                        { header: 'Çalışan Sayısı', property: 'employeeCount' as keyof BranchExcelType, width: 20 },
                        { header: 'IP Adresleri', property: 'ipAddresses' as keyof BranchExcelType, width: 40 },
                        { header: 'Oluşturulma Tarihi', property: 'createdAt' as keyof BranchExcelType, width: 25 }
                      ];

                      // Excel için formatlanmış veri
                      const formattedData = shops.map(shop => ({
                        id: shop.id,
                        name: shop.name || '-',
                        address: shop.address || '-',
                        phone: shop.phone || '-',
                        employeeCount: shop.employeeCount || 0,
                        ipAddresses: shop.ipAddresses && shop.ipAddresses.length > 0 
                          ? shop.ipAddresses.join(', ') 
                          : '-',
                        createdAt: shop.createdAt ? dayjs(shop.createdAt).format('DD.MM.YYYY') : '-'
                      }));

                      // Excel dosyasını oluştur
                      exportToExcel(formattedData, 'Şube Listesi', columnsForExcel);
                    }}
                    >
                      <i className="ti ti-file-type-xls me-2 text-success" />
                      {t('common.buttons.excel')}
                    </Link>
                  </li>
                </ul>
            </div>
            )}
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>{t('branches.branchList')}</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <LoadingSpinner centered={true} />
              ) : (
                <Table dataSource={shops} columns={columns} Selection={false} />
              )}
              </div>   
            </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <FooterSection />
        </div>
      </div>

      {/* Store Modal */}
      <div className="modal fade" id="add_store_modal">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
            <div className="modal-header" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white', 
              borderRadius: '16px 16px 0 0',
              padding: '24px 32px',
              border: 'none'
            }}>
              <div className="d-flex align-items-center">
                <div style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '12px', 
                  padding: '12px', 
                  marginRight: '16px' 
                }}>
                  {selectedShop 
                    ? <EditOutlined style={{ fontSize: '24px', color: 'white' }} />
                    : <ShopOutlined style={{ fontSize: '24px', color: 'white' }} />
                  }
                </div>
                <div>
                  <Title level={3} style={{ margin: 0, color: 'white', fontWeight: '600' }}>
                    {selectedShop ? t('branches.editBranch') : t('branches.addBranch')}
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                    {selectedShop 
                      ? 'Şube bilgilerini güncelleyin ve değişikliklerinizi kaydedin'
                      : 'Yeni şube oluşturun ve gerekli bilgileri doldurun'
                    }
                  </Text>
                </div>
              </div>
              <Button
                type="text"
                data-bs-dismiss="modal"
                onClick={handleClear}
                icon={<CloseOutlined />}
                style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '8px',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px'
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.background = 'rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.background = 'rgba(255,255,255,0.2)';
                }}
              />
            </div>
            <Form layout="vertical">
              <div className="modal-body" style={{ padding: '32px', backgroundColor: '#fafafa' }}>
                
                {/* Temel Bilgiler Bölümü */}
                <Card 
                  title={
                    <div className="d-flex align-items-center">
                      <HomeOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      <span style={{ fontWeight: '600', color: '#262626' }}>Temel Bilgiler</span>
                    </div>
                  }
                  style={{ 
                    marginBottom: '24px', 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid #e8e8e8'
                  }}
                  headStyle={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    borderRadius: '12px 12px 0 0',
                    borderBottom: '1px solid #e1e8ed'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item 
                        label={
                          <span style={{ fontWeight: '500', color: '#595959' }}>
                            <HomeOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                            {t('branches.modal.name')}
                          </span>
                        } 
                        required
                        style={{ marginBottom: '20px' }}
                      >
                        <Input
                          size="large"
                          value={formState.name}
                          onChange={(e) =>
                            setFormState({ ...formState, name: e.target.value })
                          }
                          placeholder="Şube adını giriniz"
                          style={{ 
                            borderRadius: '8px',
                            border: '2px solid #e8e8e8',
                            fontSize: '14px',
                            boxShadow: 'none'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#1890ff';
                            e.target.style.boxShadow = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e8e8e8';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        label={
                          <span style={{ fontWeight: '500', color: '#595959' }}>
                            <PhoneOutlined style={{ marginRight: '6px', color: '#52c41a' }} />
                            {t('branches.modal.phone')}
                          </span>
                        } 
                        required
                        style={{ marginBottom: '20px' }}
                      >
                        <Input
                          size="large"
                          value={formState.phone}
                          onChange={(e) =>
                            setFormState({ ...formState, phone: e.target.value })
                          }
                          placeholder="0532 123 45 67"
                          style={{ 
                            borderRadius: '8px',
                            border: '2px solid #e8e8e8',
                            fontSize: '14px',
                            boxShadow: 'none'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#52c41a';
                            e.target.style.boxShadow = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e8e8e8';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                {/* IP Adresi Yönetimi */}
                <Card 
                  title={
                    <div className="d-flex align-items-center">
                      <GlobalOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
                      <span style={{ fontWeight: '600', color: '#262626' }}>IP Adresi Yönetimi</span>
                      <Text style={{ 
                        marginLeft: '12px', 
                        fontSize: '12px', 
                        color: '#8c8c8c',
                        fontWeight: 'normal'
                      }}>
                        (Opsiyonel - Sadece belirli IP'lerden erişim için)
                      </Text>
                    </div>
                  }
                  style={{ 
                    marginBottom: '24px', 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid #e8e8e8'
                  }}
                  headStyle={{ 
                    background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                    borderRadius: '12px 12px 0 0',
                    borderBottom: '1px solid #e1e8ed'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item 
                        label={
                          <span style={{ fontWeight: '500', color: '#595959' }}>
                            <PlusOutlined style={{ marginRight: '6px', color: '#722ed1' }} />
                            Yeni IP Adresi Ekle
                          </span>
                        }
                        style={{ marginBottom: '20px' }}
                      >
                        <Input.Group compact>
                          <Input
                            size="large"
                            style={{ 
                              width: 'calc(100% - 120px)',
                              borderRadius: '8px 0 0 8px',
                              border: '2px solid #e8e8e8',
                              fontSize: '14px',
                              boxShadow: 'none'
                            }}
                            value={newIpAddress}
                            onChange={(e) => setNewIpAddress(e.target.value)}
                            onPressEnter={handleAddIpAddress}
                            placeholder="Örn: 192.168.1.1"
                            onFocus={(e) => {
                              e.target.style.borderColor = '#722ed1';
                              e.target.style.boxShadow = 'none';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e8e8e8';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                          <Button
                            size="large"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddIpAddress}
                            style={{ 
                              width: '120px',
                              borderRadius: '0 8px 8px 0',
                              background: '#722ed1',
                              borderColor: '#722ed1',
                              height: '42px'
                            }}
                          >
                            Ekle
                          </Button>
                        </Input.Group>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <div style={{ 
                        background: '#fafafa', 
                        borderRadius: '8px', 
                        padding: '16px',
                        border: '1px dashed #d9d9d9',
                        minHeight: '80px'
                      }}>
                        <Text strong style={{ 
                          display: 'block', 
                          marginBottom: '12px', 
                          color: '#595959',
                          fontSize: '13px'
                        }}>
                          📋 Kayıtlı IP Adresleri:
                        </Text>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '8px',
                          minHeight: '32px'
                        }}>
                          {formState.ipAddresses && formState.ipAddresses.length > 0 ? (
                            formState.ipAddresses.map((ip, index) => (
                              <Tag
                                key={`${ip}-${index}`}
                                closable
                                onClose={(e) => {
                                  e.preventDefault();
                                  handleRemoveIpAddress(ip);
                                }}
                                style={{
                                  background: '#722ed1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '4px 12px',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                                closeIcon={
                                  <CloseOutlined style={{ 
                                    color: '#ff4d4f', 
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: '30%',
                                    width: '16px',
                                    height: '16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    marginLeft: '6px',
                                    padding: '2px'
                                  }} />
                                }
                              >
                                🌐 {ip}
                              </Tag>
                            ))
                          ) : (
                            <div style={{ 
                              textAlign: 'center', 
                              width: '100%', 
                              padding: '20px',
                              color: '#bfbfbf'
                            }}>
                              <GlobalOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                              <br />
                              <Text type="secondary" style={{ fontSize: '13px' }}>
                                Henüz IP adresi eklenmemiş. Tüm IP'lerden erişim açık.
                              </Text>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>

                {/* Adres ve Konum Bilgileri */}
                <Card 
                  title={
                    <div className="d-flex align-items-center">
                      <EnvironmentOutlined style={{ marginRight: '8px', color: '#fa541c' }} />
                      <span style={{ fontWeight: '600', color: '#262626' }}>Adres ve Konum Bilgileri</span>
                    </div>
                  }
                  style={{ 
                    marginBottom: '24px', 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid #e8e8e8'
                  }}
                  headStyle={{ 
                    background: 'linear-gradient(135deg, #fff7e6 0%, #ffd8bf 100%)',
                    borderRadius: '12px 12px 0 0',
                    borderBottom: '1px solid #e1e8ed'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item 
                        label={
                          <span style={{ fontWeight: '500', color: '#595959' }}>
                            <PushpinOutlined style={{ marginRight: '6px', color: '#fa541c' }} />
                            {t('branches.modal.address')}
                          </span>
                        } 
                        required
                        style={{ marginBottom: '20px' }}
                      >
                        <Space.Compact style={{ width: '100%', position: 'relative', zIndex: isMapVisible ? 1000 : 'auto' }}>
                          <AutoComplete
                              style={{ width: 'calc(100% - 160px)' }}
                              size="large"
                              getPopupContainer={(trigger) => {
                                // Modal body'yi bul
                                const modalBody = trigger.closest('.modal-body');
                                return modalBody || document.body;
                              }}
                              dropdownStyle={{ zIndex: 1050 }}
                              dropdownClassName="address-autocomplete-dropdown"
                              options={isSearchingAddress ? [{ value: 'loading', label: <div style={{ textAlign: 'center' }}><Spin size="small" /></div>, disabled: true }] : addressOptions}
                              onSearch={handleAddressSearch}
                              onSelect={onAddressSelect}
                              value={formState.address}
                              onChange={(value) => setFormState({ ...formState, address: value })}
                          >
                              <Input
                                  placeholder="Şube adresini giriniz veya arayın..."
                                  style={{ 
                                    borderRadius: '8px 0 0 8px',
                                    border: '2px solid #e8e8e8',
                                    fontSize: '14px',
                                    boxShadow: 'none'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#fa541c';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#e8e8e8';
                                    e.target.style.boxShadow = 'none';
                                  }}
                              />
                          </AutoComplete>
                          <Button 
                            size="large"
                            icon={<EnvironmentOutlined />} 
                            onClick={() => setIsMapVisible(!isMapVisible)}
                            style={{ 
                              width: '160px',
                              borderRadius: '0 8px 8px 0',
                              background: isMapVisible ? '#fa541c' : '#fff',
                              borderColor: '#fa541c',
                              color: isMapVisible ? '#fff' : '#fa541c',
                              height: '35px', // Input ile aynı yükseklik
                              fontWeight: '500',
                              border: '2px solid #fa541c' // Input ile aynı border kalınlığı
                            }}
                          >
                            {isMapVisible ? ' Haritayı Gizle' : ' Haritayı Göster'}
                          </Button>
                        </Space.Compact>
                      </Form.Item>
                    </Col>
                    
                    {isMapVisible && (
                      <Col span={24}>
                        <div style={{ 
                          background: '#fff', 
                          borderRadius: '12px', 
                          padding: '16px',
                          border: '1px solid #e8e8e8',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '12px',
                            padding: '12px 16px',
                            background: 'linear-gradient(135deg, #fff7e6 0%, #ffd8bf 100%)',
                            borderRadius: '8px'
                          }}>
                            <EnvironmentOutlined style={{ 
                              fontSize: '18px', 
                              color: '#fa541c', 
                              marginRight: '8px' 
                            }} />
                            <Text strong style={{ color: '#262626' }}>
                              Harita Üzerinde Konum Seçin
                            </Text>
                            <Text style={{ 
                              marginLeft: '12px', 
                              fontSize: '12px', 
                              color: '#8c8c8c' 
                            }}>
                              Harita üzerine tıklayarak konumu işaretleyin
                            </Text>
                          </div>
                          
                          <MapContainer 
                            center={
                              formState.latitude && formState.longitude 
                                ? [formState.latitude, formState.longitude]
                                : markerPosition || [39.925533, 32.866287]
                            } 
                            zoom={
                              (formState.latitude && formState.longitude) || markerPosition ? 16 : 6
                            } 
                            key={`${formState.latitude}-${formState.longitude}-${mapKey}`}
                            style={{ 
                              height: '400px', 
                              width: '100%', 
                              borderRadius: '12px',
                              border: '2px solid #e8e8e8',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              zIndex: 1,
                              position: 'relative'
                            }}
                            scrollWheelZoom={true}
                            doubleClickZoom={true}
                          >
                            <YandexTileLayer />
                            <MapClickHandler />
                            {(formState.latitude && formState.longitude) ? (
                              <Marker position={[formState.latitude, formState.longitude]}></Marker>
                            ) : (
                              markerPosition && <Marker position={markerPosition}></Marker>
                            )}
                          </MapContainer>
                          
                          <div style={{ 
                            marginTop: '12px', 
                            padding: '12px',
                            background: '#f6ffed',
                            borderRadius: '6px',
                            border: '1px solid #b7eb8f'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                              <Text style={{ fontSize: '12px', color: '#52c41a', flex: 1 }}>
                                💡 <strong>İpucu:</strong> Harita üzerine tıklayarak şubenizin tam konumunu işaretleyin. 
                                Mouse tekerleği ile yakınlaştırabilir, çift tıklayarak da yakınlaştırabilirsiniz.
                              </Text>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Button 
                                  size="small" 
                                  onClick={() => {
                                    if (formState.latitude && formState.longitude) {
                                      // Harita merkezini güncellemek için key değiştir
                                      setMapKey(prev => prev + 1);
                                      setMarkerPosition([formState.latitude, formState.longitude]);
                                      message.success('Harita koordinatlara merkezlendi!');
                                    } else {
                                      message.warning('Önce koordinatları girin!');
                                    }
                                  }}
                                  style={{ fontSize: '11px' }}
                                >
                                  🎯 Merkeze Al
                                </Button>
                                {navigator.geolocation && (
                                  <Button 
                                    size="small" 
                                    onClick={async () => {
                                      const geoDebugTag = "[BranchLocationDebug]";
                                      const requestStartedAt = Date.now();
                                      let permissionState = "unknown";

                                      try {
                                        if (navigator.permissions?.query) {
                                          const permissionResult = await navigator.permissions.query({
                                            name: "geolocation",
                                          });
                                          permissionState = permissionResult.state;
                                        }
                                      } catch (permissionError) {
                                        console.warn(`${geoDebugTag} Permission query failed`, permissionError);
                                      }

                                      console.info(`${geoDebugTag} getCurrentPosition request started`, {
                                        permissionState,
                                        options: {
                                          enableHighAccuracy: true,
                                          timeout: 10000,
                                          maximumAge: 0,
                                        },
                                      });

                                      navigator.geolocation.getCurrentPosition(
                                        (position) => {
                                          const lat = position.coords.latitude;
                                          const lng = position.coords.longitude;

                                          console.info(`${geoDebugTag} getCurrentPosition success`, {
                                            latitude: lat,
                                            longitude: lng,
                                            accuracy: position.coords.accuracy,
                                            altitude: position.coords.altitude,
                                            heading: position.coords.heading,
                                            speed: position.coords.speed,
                                            elapsedMs: Date.now() - requestStartedAt,
                                            permissionState,
                                          });
                                          
                                          // Konumu güncelle
                                          setFormState(prev => ({
                                            ...prev,
                                            latitude: lat,
                                            longitude: lng,
                                          }));
                                          
                                          // Marker'ı güncelle
                                          setMarkerPosition([lat, lng]);
                                          
                                          // Adresi güncelle
                                          updateAddressFromCoordinates(lat, lng);
                                          
                                          // Başarı mesajı
                                          message.success('Mevcut konumunuz alındı!');
                                        },
                                        (error) => {
                                          console.error(`${geoDebugTag} getCurrentPosition failed`, {
                                            code: error.code,
                                            message: error.message,
                                            elapsedMs: Date.now() - requestStartedAt,
                                            permissionState,
                                          });
                                          let errorMessage = 'Konum alınamadı. ';
                                          switch(error.code) {
                                            case error.PERMISSION_DENIED:
                                              errorMessage += 'Konum izni verilmedi.';
                                              break;
                                            case error.POSITION_UNAVAILABLE:
                                              errorMessage += 'Konum bilgisi mevcut değil.';
                                              break;
                                            case error.TIMEOUT:
                                              errorMessage += 'Konum alma işlemi zaman aşımına uğradı.';
                                              break;
                                            default:
                                              errorMessage += 'Bilinmeyen bir hata oluştu.';
                                              break;
                                          }
                                          message.error(errorMessage);
                                        },
                                        {
                                          enableHighAccuracy: true,
                                          timeout: 10000,
                                          maximumAge: 0
                                        }
                                      );
                                    }}
                                    style={{ fontSize: '11px' }}
                                  >
                                    📍 Konumum
                                  </Button>
                                )}
                              </div>
                            </div>
                            {(formState.latitude && formState.longitude) && (
                              <div style={{ marginTop: '8px', fontSize: '11px', color: '#595959' }}>
                                📍 <strong>Seçili Konum:</strong> Enlem: {formState.latitude}, Boylam: {formState.longitude}
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card>

                {/* Konum Bilgileri */}
                <Card 
                  title={
                    <div className="d-flex align-items-center">
                      <GlobalOutlined style={{ marginRight: '8px', color: '#13c2c2' }} />
                      <span style={{ fontWeight: '600', color: '#262626' }}>Konum Bilgileri</span>
                      <Text style={{ 
                        marginLeft: '12px', 
                        fontSize: '12px', 
                        color: '#8c8c8c',
                        fontWeight: 'normal'
                      }}>
                        (Haritadan bir konum seçtiğinizde enlem ve boylam bilgileri otomatik olarak doldurulur. Gerekirse bu alanları manuel olarak düzenleyebilirsiniz.)
                      </Text>
                    </div>
                  }
                  style={{ 
                    marginBottom: '24px', 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '2px solid #13c2c2'
                  }}
                  headStyle={{ 
                    background: 'linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)',
                    borderRadius: '12px 12px 0 0',
                    borderBottom: '1px solid #e1e8ed'
                  }}
                  bodyStyle={{ 
                    padding: '24px',
                    background: '#fff'
                  }}
                >
                  <div style={{ 
                    background: '#e6fffb', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    marginBottom: '24px',
                    border: '1px solid #87e8de'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <GlobalOutlined style={{ color: '#13c2c2', marginRight: '8px' }} />
                      <Text strong style={{ color: '#13c2c2' }}>
                        Şube Konum Ayarları
                      </Text>
                    </div>
                    <Text style={{ fontSize: '12px', color: '#595959' }}>
                      Şubenizin koordinat bilgilerini girin. Bu bilgiler konum tabanlı çıkış için kullanılacaktır.
                      Harita üzerinden konum seçimi yapabilir veya manuel koordinat girebilirsiniz.
                    </Text>
                  </div>

                  {/* Konum Tabanlı Giriş-Çıkış Switch */}
                  <Row gutter={24} style={{ marginBottom: '24px' }}>
                    <Col span={24}>
                      <div style={{ 
                        background: formState.enableLocationBasedCheckout ? '#f6ffed' : '#fff2e8', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        border: formState.enableLocationBasedCheckout ? '1px solid #b7eb8f' : '1px solid #ffd591',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <EnvironmentOutlined style={{ 
                            color: formState.enableLocationBasedCheckout ? '#52c41a' : '#fa8c16', 
                            fontSize: '18px', 
                            marginRight: '12px' 
                          }} />
                          <div>
                            <Text strong style={{ 
                              color: formState.enableLocationBasedCheckout ? '#52c41a' : '#fa8c16',
                              display: 'block',
                              fontSize: '14px'
                            }}>
                              Konum Tabanlı Giriş Çıkış
                            </Text>
                            <Text style={{ 
                              fontSize: '12px', 
                              color: '#8c8c8c',
                              display: 'block',
                              marginTop: '2px'
                            }}>
                              {formState.enableLocationBasedCheckout 
                                ? 'Çalışanlar sadece belirlenen konum yakınında giriş/çıkış yapabilir'
                                : 'Çalışanlar herhangi bir konumdan giriş/çıkış yapabilir'
                              }
                            </Text>
                          </div>
                        </div>
                        <Switch
                          checked={formState.enableLocationBasedCheckout}
                          onChange={(checked) => {
                            setFormState({ 
                              ...formState, 
                              enableLocationBasedCheckout: checked,
                              // Eğer kapatılıyorsa koordinatları temizle
                              ...(checked ? {} : { 
                                latitude: undefined, 
                                longitude: undefined, 
                                locationTolerance: 1000 
                              })
                            });
                            if (!checked) {
                              setMarkerPosition(null);
                              setIsMapVisible(false);
                            }
                          }}
                          size="default"
                          style={{
                            background: formState.enableLocationBasedCheckout ? '#52c41a' : '#d9d9d9'
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Koordinat Alanları - Sadece enableLocationBasedCheckout true ise göster */}
                  {formState.enableLocationBasedCheckout && (
                    <Row gutter={24}>
                      <Col span={8}>
                        <Form.Item 
                          label={
                            <span style={{ fontWeight: '500', color: '#595959' }}>
                              <GlobalOutlined style={{ marginRight: '6px', color: '#13c2c2' }} />
                              Enlem (Latitude)
                            </span>
                          }
                          style={{ marginBottom: '20px' }}
                        >
                          <InputNumber
                          size="large"
                          style={{ 
                            width: '100%',
                            borderRadius: '8px',
                            border: '2px solid #e8e8e8',
                            boxShadow: 'none'
                          }}
                          value={formState.latitude}
                          onChange={(value) => {
                            setFormState({ ...formState, latitude: value === null ? undefined : value });
                            if (value && formState.longitude) {
                              setMarkerPosition([value, formState.longitude]);
                              // Anlık adres güncelleme
                              updateAddressFromCoordinates(value, formState.longitude!);
                            }
                          }}
                          placeholder="41.015137"
                          precision={6}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#13c2c2';
                            e.target.style.boxShadow = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e8e8e8';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item 
                        label={
                          <span style={{ fontWeight: '500', color: '#595959' }}>
                            <GlobalOutlined style={{ marginRight: '6px', color: '#13c2c2' }} />
                            Boylam (Longitude)
                          </span>
                        }
                        style={{ marginBottom: '20px' }}
                      >
                        <InputNumber
                          size="large"
                          style={{ 
                            width: '100%',
                            borderRadius: '8px',
                            border: '2px solid #e8e8e8',
                            boxShadow: 'none'
                          }}
                          value={formState.longitude}
                          onChange={(value) => {
                            setFormState({ ...formState, longitude: value === null ? undefined : value });
                            if (formState.latitude && value) {
                              setMarkerPosition([formState.latitude, value]);
                              // Anlık adres güncelleme
                              updateAddressFromCoordinates(formState.latitude!, value);
                            }
                          }}
                          placeholder="28.979530"
                          precision={6}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#13c2c2';
                            e.target.style.boxShadow = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e8e8e8';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item 
                        label={
                          <span style={{ fontWeight: '500', color: '#595959' }}>
                            <EnvironmentOutlined style={{ marginRight: '6px', color: '#13c2c2' }} />
                            Tolerans Mesafesi
                          </span>
                        }
                        style={{ marginBottom: '20px' }}
                      >
                        <InputNumber
                          size="large"
                          addonAfter={
                            <span style={{ color: '#13c2c2', fontWeight: '500' }}>
                              metre
                            </span>
                          }
                          style={{ 
                            width: '100%',
                            borderRadius: '8px',
                            border: '2px solid #e8e8e8',
                            boxShadow: 'none'
                          }}
                          value={formState.locationTolerance}
                          onChange={(value) => setFormState({ ...formState, locationTolerance: value === null ? undefined : value })}
                          placeholder="1000"
                          min={50}
                          max={5000}
                          step={50}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#13c2c2';
                            e.target.style.boxShadow = 'none';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e8e8e8';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Item>
                    </Col>

                    {formState.latitude && formState.longitude && formState.locationTolerance && (
                      <div style={{ 
                        background: '#f6ffed', 
                        padding: '12px', 
                        borderRadius: '6px',
                        border: '1px solid #b7eb8f'
                      }}>
                        <Text style={{ fontSize: '12px', color: '#52c41a' }}>
                          ✅ <strong>Konum Ayarları Tamamlandı:</strong> 
                          Çalışanlar {formState.locationTolerance}m yarıçap içinde çıkış yapabilecekler.
                        </Text>
                      </div>
                    )}
                    </Row>
                  )}
                </Card>
              </div>
            </Form>
            <div className="modal-footer" style={{ 
              padding: '24px 32px', 
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: '0 0 16px 16px',
              borderTop: '1px solid #e8e8e8'
            }}>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {selectedShop ? (
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      🔄 <strong>Düzenleniyor:</strong> {selectedShop.name}
                    </Text>
                  ) : (
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      ➕ <strong>Yeni şube ekleniyor</strong>
                    </Text>
                  )}
                </div>
                {user?.permissions?.includes(PermissionType.EDIT_BRANCHES) && (
                <Space size="middle">
                  <Button
                    size="large"
                    onClick={handleClear}
                    data-bs-dismiss="modal"
                    style={{
                      borderRadius: '8px',
                      border: '2px solid #d9d9d9',
                      background: '#fff',
                      color: '#595959',
                      fontWeight: '500',
                      height: '42px',
                      minWidth: '120px'
                    }}
                    onMouseEnter={(e) => {
                      const target = e.target as HTMLButtonElement;
                      target.style.borderColor = '#ff4d4f';
                      target.style.color = '#ff4d4f';
                    }}
                    onMouseLeave={(e) => {
                      const target = e.target as HTMLButtonElement;
                      target.style.borderColor = '#d9d9d9';
                      target.style.color = '#595959';
                    }}
                  >
                    ❌ İptal
                  </Button>
                  
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleSave}
                    data-bs-dismiss="modal"
                    style={{
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      fontWeight: '600',
                      height: '42px',
                      minWidth: '140px',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      const target = e.target as HTMLButtonElement;
                      target.style.transform = 'translateY(-2px)';
                      target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                      const target = e.target as HTMLButtonElement;
                      target.style.transform = 'translateY(0px)';
                      target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }}
                  >
                    {selectedShop ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </Space>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* AutoComplete dropdown z-index fix */}
      <style>{`
        .address-autocomplete-dropdown {
          z-index: 1050 !important;
        }
        .ant-select-dropdown {
          z-index: 1050 !important;
        }
        #add_store_modal .leaflet-container {
          z-index: 1 !important;
        }
        /* Leaflet pane'lerine (tile/marker vb.) z-index verme: hepsi aynı seviyede olunca
           DOM'da en sona eklenen Yandex katmanı marker'ın üstüne çıkıp pin'i gizliyor.
           Container'daki z-index:1 zaten haritayı dropdown'ların altında tutuyor. */
      `}</style>
    </>
  );
};

export default Shop;
